<template>
  <div class="geo-kpis">
    <div class="kpis">
      <KPI
        v-for="kpi in kpis"
        :key="kpi.label"
        :label="kpi.label"
        :value="kpi.value"
        :icon="kpi.icon"
        :big-icon="false"
        :is-loading="kpi.isLoading"
        :use-flex-basis="false"
        :is-money="kpi.isMoney"
        trend-format="+0a"
        trend-style="font-size: 0.9em"
        value-style="font-size: 2.4rem;"
        label-style="font-size: 1rem;" />
    </div>
    <small class="disclaimer">
      {{ $t('messages.estimatedScreenCount') }}
    </small>
  </div>
</template>

<script setup lang="ts">
  import geojsonExtent from '@mapbox/geojson-extent';
  import turfDistance from '@turf/distance';
  import { point, points, polygons, type Units } from '@turf/helpers';
  import pointsWithinPolygon from '@turf/points-within-polygon';
  import axios, { CancelTokenSource } from 'axios';
  import { computed, markRaw, onMounted, ref, watch } from 'vue';

  import { IconColoredScreen } from '@/common/icons/colored';
  import { FilterLogicalOperator, Unit } from '@/common/models';
  import Geofence from '@/common/models/geo/Geofence';
  import Filter from '@/common/models/query-params/Filter';
  import FilterCollection from '@/common/models/query-params/FilterCollection';
  import * as ComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import QueryParams from '@/common/models/query-params/QueryParams';
  import UnitCluster from '@/common/models/unit/UnitCluster';
  import { formatNumber } from '@/globals';
  import KPI from '@/org-app/components/controls/KPI.vue';
  import { mapOrgAppActions } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface Props {
    unitClusters: UnitCluster[];
    geofences: Geofence[];
  }

  interface IKpi {
    label: string;
    value: string;
    icon: unknown;
    isLoading: boolean;
    isMoney: boolean;
  }

  interface IGeofenceItem {
    id: number;
    latitude: number;
    longitude: number;
  }

  const props = defineProps<Props>();

  const { getUnits } = mapOrgAppActions();

  const unitsInGeofences = ref([]);
  const isEdsLoading = ref<boolean>(false);
  let getUnitsCancelTokenSource: CancelTokenSource = null;

  const kpis = computed<IKpi[]>(() => [
    {
      label: i18n.global.t('names.screen', unitsInGeofences.value.length),
      value: formatNumber(unitsInGeofences.value.length || 0, '0,0'),
      icon: markRaw(IconColoredScreen),
      isLoading: isEdsLoading.value,
      isMoney: false,
    },
  ]);

  function itemsInGeofence(geofenceItems: IGeofenceItem[]) {
    if (!geofenceItems?.length) return [];

    const geofencesWithCircles = props.geofences.filter((x) => x.isPoint && x.radius);
    const geofencesWithPolygons = props.geofences.filter((x) => !(x.isPoint && x.radius));

    const geofencesPolygons = geofencesWithPolygons.map((g) => g?.geometry?.coordinates);
    const polys = polygons(geofencesPolygons);

    const coordinates = geofenceItems.map((u) => [u.longitude, u.latitude]);
    const pts = points(coordinates);

    const distanceOptions = { units: 'kilometers' as Units };
    const withinPolygons = pointsWithinPolygon(pts, polys);
    const withinCircles = {
      type: 'FeatureCollection',
      features: pts.features.filter((pt) =>
        geofencesWithCircles.some((g) => {
          const geofenceCenter = point([g.lng, g.lat]);
          const distance = turfDistance(pt, geofenceCenter, distanceOptions);
          return distance <= g.radius;
        }),
      ),
    };
    const within = {
      type: 'FeatureCollection',
      features: [...withinCircles.features, ...withinPolygons.features],
    };

    return within.features;
  }

  function areClustersInsideGeofence() {
    const geofenceItems: IGeofenceItem[] = props.unitClusters?.map((uc) => ({
      id: uc.id,
      latitude: uc.latitude,
      longitude: uc.longitude,
    }));

    return itemsInGeofence(geofenceItems).length > 0;
  }

  function getTargetedUnitIds(units: Unit[]) {
    const geofenceItems: IGeofenceItem[] = units?.map((u) => ({
      id: u.id,
      latitude: u.latitude,
      longitude: u.longitude,
    }));

    return itemsInGeofence(geofenceItems);
  }

  async function fetchUnits(): Promise<Unit[]> {
    if (!areClustersInsideGeofence()) return [];

    const bbox = geojsonExtent({
      type: 'FeatureCollection',
      features: props.geofences.map((g) => g.feature),
    });
    const lat = [bbox[1], bbox[3]];
    const lng = [bbox[0], bbox[2]];
    const minLat = Math.min(...lat);
    const maxLat = Math.max(...lat);
    const minLng = Math.min(...lng);
    const maxLng = Math.max(...lng);

    const queryParams = QueryParams.for({
      top: null,
      include: ['id', 'latitude', 'longitude'],
      filterCollection: new FilterCollection(
        [
          new Filter('latitude', [minLat], ComparisonOperator.GREATER_THAN_OR_EQUAL),
          new Filter('latitude', [maxLat], ComparisonOperator.LESS_THAN_OR_EQUAL),
          new Filter('longitude', [minLng], ComparisonOperator.GREATER_THAN_OR_EQUAL),
          new Filter('longitude', [maxLng], ComparisonOperator.LESS_THAN_OR_EQUAL),
        ],
        FilterLogicalOperator.AND,
      ),
    });

    if (getUnitsCancelTokenSource != null) {
      getUnitsCancelTokenSource.cancel();
    }
    getUnitsCancelTokenSource = axios.CancelToken.source();

    const response = await getUnits({ queryParams, cancelTokenSource: getUnitsCancelTokenSource });
    return response?.items;
  }

  async function computeStats(): Promise<void> {
    if (!props.geofences?.length || !props.unitClusters?.length) {
      unitsInGeofences.value = [];
      return;
    }

    isEdsLoading.value = true;

    // clusters positions are not accurate, fetch the units within the geofences bounds
    const units = await fetchUnits();

    if (units == null) return; // Request was cancelled.

    // make sure the fetched units are indeed within the geofences, then filter out to get the unique ids
    unitsInGeofences.value = getTargetedUnitIds(units);

    isEdsLoading.value = false;
  }

  watch(
    () => props.unitClusters,
    () => {
      computeStats();
    },
  );
  watch(
    () => props.geofences,
    () => {
      computeStats();
    },
  );

  onMounted(() => {
    if (props.unitClusters?.length && props.geofences?.length) {
      computeStats();
    }
  });
</script>

<style scoped lang="scss">
  .geo-kpis {
    display: flex;
    flex-direction: column;
    align-items: end;
  }
</style>
